import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { PageHeader } from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import PriorityChip from "@/components/PriorityChip";
import { fmtDate } from "@/lib/dates";
import { toast } from "sonner";
import { parseApiError } from "@/lib/apiErrors";
import { Plus, Search, Copy, Archive, ArchiveRestore, Trash2, Clock, LayoutTemplate, Pencil } from "lucide-react";

const BLANK = {
  title: "", description: "", priority: "Medium", default_assignee_ids: [],
  trigger_day_of_month: "", due_after_days: "", recurrence: "", notes: "",
};

export default function TaskTemplates() {
  const { user } = useAuth();
  const isAdmin = user?.role === "Admin";
  const [templates, setTemplates] = useState([]);
  const [users, setUsers] = useState([]);
  const [search, setSearch] = useState("");
  const [showArchived, setShowArchived] = useState(false);
  const [editing, setEditing] = useState(null); // null | "new" | template object

  const load = () => {
    api.get("/task-templates", { params: { include_archived: true } })
      .then((r) => setTemplates(r.data)).catch(() => {});
  };
  useEffect(() => {
    load();
    api.get("/users").then((r) => setUsers((r.data || []).filter((u) => u.active !== false))).catch(() => {});
  }, []);

  const shown = useMemo(() => {
    const q = search.trim().toLowerCase();
    return templates.filter((t) => (showArchived ? true : !t.archived))
      .filter((t) => !q || t.title.toLowerCase().includes(q) || (t.recurrence || "").toLowerCase().includes(q)
        || (t.description || "").toLowerCase().includes(q));
  }, [templates, search, showArchived]);

  const duplicate = async (t) => {
    await api.post(`/task-templates/${t.id}/duplicate`);
    toast.success("Template duplicated");
    load();
  };
  const toggleArchive = async (t) => {
    await api.patch(`/task-templates/${t.id}`, { archived: !t.archived });
    toast.success(t.archived ? "Template restored" : "Template archived");
    load();
  };
  const del = async (t) => {
    if (!window.confirm(`Delete template "${t.title}"? Existing tasks created from it are not affected.`)) return;
    await api.delete(`/task-templates/${t.id}`);
    toast.success("Template deleted");
    load();
  };

  const assigneeNames = (ids) =>
    (ids || []).map((id) => users.find((u) => u.user_id === id)?.name || id).join(", ") || "—";

  return (
    <div data-testid="task-templates-page">
      <PageHeader
        title="Task Templates"
        subtitle="Reusable Drafts"
        action={
          <Button className="bg-[#0a2540] hover:bg-[#0f2f50] rounded-sm" onClick={() => setEditing("new")} data-testid="btn-new-template">
            <Plus size={14} className="mr-1.5" /> New Template
          </Button>
        }
      />
      <div className="px-4 sm:px-8 py-5">
        <div className="flex flex-wrap items-center gap-3 mb-4">
          <div className="relative">
            <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search templates…" className="pl-8 h-9 w-56 sm:w-72 rounded-sm" data-testid="template-search" />
          </div>
          <label className="flex items-center gap-1.5 text-xs text-slate-600 cursor-pointer">
            <input type="checkbox" checked={showArchived} onChange={(e) => setShowArchived(e.target.checked)} data-testid="toggle-show-archived" />
            Show archived
          </label>
          <div className="flex-1" />
          <p className="text-[11px] text-slate-400 hidden md:block">
            Templates never create tasks by themselves — use them from any client project's Tasks tab.
          </p>
        </div>

        {shown.length === 0 ? (
          <div className="border border-dashed border-slate-300 rounded-sm py-16 text-center">
            <LayoutTemplate size={28} className="mx-auto text-slate-300 mb-3" />
            <div className="text-sm text-slate-500">No templates yet.</div>
            <div className="text-xs text-slate-400 mt-1">Create drafts for repetitive monthly work — GST filing, TDS payment, payroll…</div>
          </div>
        ) : (
          <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
            {shown.map((t) => (
              <div key={t.id} className={`border rounded-sm p-4 bg-white ${t.archived ? "border-slate-200 opacity-60" : "border-slate-200 hover:border-[#0a2540]/40"} transition-colors`} data-testid={`template-card-${t.id}`}>
                <div className="flex items-start gap-2">
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-semibold text-[#0a2540] truncate" style={{ fontFamily: "Outfit" }}>{t.title}</div>
                    <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mt-1.5 text-[10px] text-slate-500">
                      <PriorityChip priority={t.priority} />
                      {t.recurrence && <span className="bg-slate-100 rounded-full px-2 py-0.5">{t.recurrence}</span>}
                      {t.archived && <span className="bg-slate-100 text-slate-500 rounded-full px-2 py-0.5">Archived</span>}
                    </div>
                  </div>
                </div>
                {t.description && <p className="text-[11px] text-slate-500 mt-2 line-clamp-2">{t.description}</p>}
                <div className="mt-3 space-y-1 text-[11px] text-slate-600">
                  {t.trigger_day_of_month && (
                    <div className="flex items-center gap-1.5">
                      <Clock size={10} className="text-amber-600" />
                      Triggers day <strong>{t.trigger_day_of_month}</strong> of each month
                      {t.suggested?.trigger_date && <span className="text-slate-400">(next: {fmtDate(t.suggested.trigger_date)})</span>}
                    </div>
                  )}
                  {t.due_after_days != null && t.due_after_days !== "" && (
                    <div>Due <strong>{t.due_after_days} day{t.due_after_days === 1 ? "" : "s"}</strong> after trigger</div>
                  )}
                  <div className="truncate">Default assignees: {assigneeNames(t.default_assignee_ids)}</div>
                </div>
                <div className="mt-3 pt-2.5 border-t border-slate-100 flex items-center justify-between">
                  <span className="text-[10px] text-slate-400">Used {t.usage_count || 0}×</span>
                  <div className="flex items-center gap-1">
                    <IconBtn title="Edit" onClick={() => setEditing(t)} testId={`btn-edit-${t.id}`}><Pencil size={12} /></IconBtn>
                    <IconBtn title="Duplicate" onClick={() => duplicate(t)} testId={`btn-duplicate-${t.id}`}><Copy size={12} /></IconBtn>
                    <IconBtn title={t.archived ? "Restore" : "Archive"} onClick={() => toggleArchive(t)} testId={`btn-archive-${t.id}`}>
                      {t.archived ? <ArchiveRestore size={12} /> : <Archive size={12} />}
                    </IconBtn>
                    {isAdmin && (
                      <IconBtn title="Delete" onClick={() => del(t)} danger testId={`btn-delete-${t.id}`}><Trash2 size={12} /></IconBtn>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {editing && (
        <TemplateDialog
          template={editing === "new" ? null : editing}
          users={users}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); }}
        />
      )}
    </div>
  );
}

function IconBtn({ title, onClick, children, danger, testId }) {
  return (
    <button
      type="button"
      title={title}
      onClick={onClick}
      className={`p-1.5 rounded-sm border border-slate-200 ${danger ? "text-slate-400 hover:text-red-600 hover:border-red-300" : "text-slate-500 hover:text-[#0a2540] hover:border-[#0a2540]"}`}
      data-testid={testId}
    >
      {children}
    </button>
  );
}

function TemplateDialog({ template, users, onClose, onSaved }) {
  const [form, setForm] = useState(template ? {
    title: template.title, description: template.description || "", priority: template.priority || "Medium",
    default_assignee_ids: template.default_assignee_ids || [],
    trigger_day_of_month: template.trigger_day_of_month ?? "", due_after_days: template.due_after_days ?? "",
    recurrence: template.recurrence || "", notes: template.notes || "",
  } : BLANK);

  const toggleAssignee = (uid) => {
    setForm((f) => {
      const ids = new Set(f.default_assignee_ids || []);
      if (ids.has(uid)) ids.delete(uid); else ids.add(uid);
      return { ...f, default_assignee_ids: [...ids] };
    });
  };

  const save = async () => {
    if (!form.title.trim()) { toast.error("Title is required"); return; }
    const body = {
      ...form,
      trigger_day_of_month: form.trigger_day_of_month === "" ? null : Number(form.trigger_day_of_month),
      due_after_days: form.due_after_days === "" ? null : Number(form.due_after_days),
    };
    try {
      if (template) await api.patch(`/task-templates/${template.id}`, body);
      else await api.post("/task-templates", body);
      toast.success(template ? "Template updated" : "Template created");
      onSaved();
    } catch (e) { toast.error(parseApiError(e, "Failed to save")); }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="rounded-sm max-h-[90vh] overflow-y-auto" data-testid="template-dialog">
        <DialogHeader>
          <DialogTitle style={{ fontFamily: "Outfit" }}>{template ? "Edit Template" : "New Task Template"}</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label className="text-xs">Title *</Label>
            <Input className="mt-1 rounded-sm" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="e.g. GSTR-1 monthly filing" data-testid="tpl-input-title" />
          </div>
          <div>
            <Label className="text-xs">Description</Label>
            <Textarea className="mt-1 rounded-sm" rows={3} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="tpl-input-desc" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Priority</Label>
              <Select value={form.priority} onValueChange={(v) => setForm({ ...form, priority: v })}>
                <SelectTrigger className="mt-1 rounded-sm" data-testid="tpl-select-priority"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="Low">Low</SelectItem>
                  <SelectItem value="Medium">Medium</SelectItem>
                  <SelectItem value="High">High</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Recurrence label</Label>
              <Input className="mt-1 rounded-sm" value={form.recurrence} onChange={(e) => setForm({ ...form, recurrence: e.target.value })} placeholder="Monthly / Quarterly…" data-testid="tpl-input-recurrence" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Trigger day of month</Label>
              <Input type="number" min={1} max={31} className="mt-1 rounded-sm" value={form.trigger_day_of_month}
                onChange={(e) => setForm({ ...form, trigger_day_of_month: e.target.value })}
                placeholder="e.g. 5" data-testid="tpl-input-trigger-day" />
              <p className="text-[10px] text-slate-400 mt-1">Auto-fills the next upcoming date at task creation.</p>
            </div>
            <div>
              <Label className="text-xs">Due after (days)</Label>
              <Input type="number" min={0} max={365} className="mt-1 rounded-sm" value={form.due_after_days}
                onChange={(e) => setForm({ ...form, due_after_days: e.target.value })}
                placeholder="e.g. 6" data-testid="tpl-input-due-after" />
              <p className="text-[10px] text-slate-400 mt-1">Due date = trigger date + this many days.</p>
            </div>
          </div>
          <div>
            <Label className="text-xs">Default assignees</Label>
            <div className="mt-1 border border-slate-200 rounded-sm p-2 max-h-36 overflow-y-auto space-y-1" data-testid="tpl-assignees-list">
              {users.map((u) => (
                <label key={u.user_id} className="flex items-center gap-2 text-xs cursor-pointer">
                  <input type="checkbox" checked={(form.default_assignee_ids || []).includes(u.user_id)} onChange={() => toggleAssignee(u.user_id)} data-testid={`tpl-assignee-${u.user_id}`} />
                  <span className="truncate">{u.name}</span>
                  <span className="text-slate-400 font-mono-data ml-auto shrink-0">({u.role})</span>
                </label>
              ))}
            </div>
          </div>
          <div>
            <Label className="text-xs">Notes (internal)</Label>
            <Textarea className="mt-1 rounded-sm" rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} data-testid="tpl-input-notes" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" className="rounded-sm" onClick={onClose}>Cancel</Button>
          <Button className="bg-[#0a2540] hover:bg-[#0f2f50] rounded-sm" onClick={save} data-testid="tpl-btn-save">
            {template ? "Save changes" : "Create template"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
